import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X } from "lucide-react";
import { readConsent, saveConsent, applyConsent } from "@/lib/consent";
import { useT } from "@/lib/i18n";

const TXT = {
  no: {
    title: "Informasjonskapsler", close: "Lukk", more: "Les mer",
    intro: "Vi bruker nødvendige informasjonskapsler for at nettsiden skal fungere. Med ditt samtykke bruker vi også statistikk for å forstå hvordan nettsiden brukes, og markedsføring (Google Ads) for å måle om annonsene våre fører til henvendelser.",
    nec: "Nødvendige", necText: "Lagrer cookie-valget og språkvalget ditt og holder administrator innlogget. Kan ikke slås av.",
    stats: "Statistikk", statsText: "PostHog (via vår driftsleverandør Emergent): anonym besøksstatistikk og øktopptak av hvordan siden brukes. Lagrer ph_*-informasjonskapsler.",
    mkt: "Markedsføring", mktText: "Google Ads: måler om et klikk på annonsene våre fører til en sendt forespørsel (konverteringssporing). Google-taggen lastes bare hvis du slår dette på. Lagrer _gcl_*-informasjonskapsler.",
    accept: "Godta alle", reject: "Avvis valgfrie", save: "Lagre valg", customize: "Tilpass",
  },
  en: {
    title: "Cookies", close: "Close", more: "Read more",
    intro: "We use necessary cookies to make the website work. With your consent we also use statistics to understand how the website is used, and marketing (Google Ads) to measure whether our ads lead to inquiries.",
    nec: "Necessary", necText: "Stores your cookie choice and language choice and keeps the administrator signed in. Cannot be turned off.",
    stats: "Statistics", statsText: "PostHog (via our hosting provider Emergent): anonymous visitor statistics and session recordings of how the site is used. Stores ph_* cookies.",
    mkt: "Marketing", mktText: "Google Ads: measures whether a click on our ads leads to a submitted inquiry (conversion tracking). The Google tag only loads if you turn this on. Stores _gcl_* cookies.",
    accept: "Accept all", reject: "Reject optional", save: "Save choices", customize: "Customise",
  },
};

export const openCookieSettings = () => window.dispatchEvent(new Event("rbtun:open-consent"));

const Toggle = ({ on, onChange, disabled, testid, label }) => (
  <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled} onClick={() => onChange(!on)} data-testid={testid}
    className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${on ? "bg-[var(--red)]" : "bg-white/15"} ${disabled ? "opacity-60" : ""}`}>
    <span className={`absolute left-0 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${on ? "translate-x-[22px]" : "translate-x-0.5"}`} />
  </button>
);

const Row = ({ title, text, children }) => (
  <div className="flex items-start justify-between gap-4 border-t border-white/10 py-4">
    <div>
      <div className="font-display text-xl font-bold uppercase leading-none">{title}</div>
      <p className="mt-1.5 text-xs leading-relaxed text-zinc-300">{text}</p>
    </div>
    {children}
  </div>
);

const btn = "px-4 py-3 font-mono text-xs font-bold uppercase tracking-widest transition-[transform,background-color,border-color] hover:-translate-y-0.5";

export const CookieBanner = () => {
  const t = useT(TXT);
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(false);
  const [stats, setStats] = useState(false);
  const [mkt, setMkt] = useState(false);

  useEffect(() => {
    const c = readConsent();
    if (c) applyConsent(c);
    else setOpen(true);
    const onOpen = () => {
      setStats(!!readConsent()?.statistics);
      setMkt(!!readConsent()?.marketing);
      setCustom(true);
      setOpen(true);
    };
    window.addEventListener("rbtun:open-consent", onOpen);
    return () => window.removeEventListener("rbtun:open-consent", onOpen);
  }, []);

  const decide = (statistics, marketing) => {
    saveConsent({ statistics, marketing });
    setOpen(false);
    setCustom(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div role="dialog" aria-labelledby="cookie-title" data-testid="cookie-banner"
          initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }} transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          className="fixed inset-x-3 bottom-3 z-[70] mx-auto max-w-xl border border-white/10 border-t-2 border-t-[var(--red)] bg-[#0f1115]/95 p-5 shadow-2xl backdrop-blur-xl sm:inset-x-auto sm:left-5 sm:p-6"
          data-lenis-prevent>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <Cookie size={20} className="text-[var(--red)]" />
              <h2 id="cookie-title" className="font-display text-2xl font-black uppercase leading-none">{t.title}</h2>
            </div>
            {custom && readConsent() && (
              <button onClick={() => { setOpen(false); setCustom(false); }} aria-label={t.close} data-testid="cookie-close" className="text-zinc-400 hover:text-white"><X size={18} /></button>
            )}
          </div>
          <p className="mt-3 text-sm leading-relaxed text-zinc-300">
            {t.intro}{" "}
            <Link to="/personvern" className="text-white underline underline-offset-2" data-testid="cookie-privacy-link">{t.more}</Link>.
          </p>

          {custom && (
            <div className="mt-4 max-h-[45vh] overflow-y-auto" data-testid="cookie-custom-panel">
              <Row title={t.nec} text={t.necText}>
                <Toggle on disabled onChange={() => {}} label={t.nec} testid="cookie-toggle-necessary" />
              </Row>
              <Row title={t.stats} text={t.statsText}>
                <Toggle on={stats} onChange={setStats} label={t.stats} testid="cookie-toggle-statistics" />
              </Row>
              <Row title={t.mkt} text={t.mktText}>
                <Toggle on={mkt} onChange={setMkt} label={t.mkt} testid="cookie-toggle-marketing" />
              </Row>
            </div>
          )}

          <div className="mt-5 grid gap-2 sm:grid-cols-3">
            <button onClick={() => decide(true, true)} data-testid="cookie-accept-all" className={`${btn} bg-[var(--red)] text-white`}>{t.accept}</button>
            <button onClick={() => decide(false, false)} data-testid="cookie-reject" className={`${btn} border border-white/20 text-white hover:border-white/50`}>{t.reject}</button>
            {custom ? (
              <button onClick={() => decide(stats, mkt)} data-testid="cookie-save" className={`${btn} border border-white/20 text-white hover:border-white/50`}>{t.save}</button>
            ) : (
              <button onClick={() => { setStats(false); setMkt(false); setCustom(true); }} data-testid="cookie-customize" className={`${btn} border border-white/20 text-white hover:border-white/50`}>{t.customize}</button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
